import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { isEqual } from 'lodash';
import { SortableTableColumnHeader, Pagination, TYPES } from 'sarsaparilla';
import { STATE_LOADING, STATE_READY } from '../../../../ui-lottery/src/utils/async_utils';
import { fetchAssets } from '../../actions/asset';
import { fetchOrganizations } from '../../actions/organization';
import { saveSearch } from '../../actions/search';
import { StewardSearchForm } from '../../components/StewardSearchForm/StewardSearchForm';
import { AssetProps } from '../../props/asset';
import { CurrentUserProps } from '../../props/currentUser';
import { OrganizationProps } from '../../props/organization';
import { SearchProps } from '../../props/search';
import { getState } from '../../reducers';
import { getCurrentUser } from '../../reducers/currentUser';
import { getAssets, getAssetCount } from '../../reducers/assets';
import { getOrganization, getOrganizations } from '../../reducers/organizations';
import { getSearch } from '../../reducers/search';
import { flattenOrg, flattenOrgs, orgsToOptions } from '../../utils/organization';

import styles from './StewardSearchPage.scss';

const propTypes = {
  history: TYPES.HISTORY,
  match: TYPES.MATCH,
  currentUser: CurrentUserProps,
  searchQuery: SearchProps,
  organization: OrganizationProps,
  organizations: PropTypes.arrayOf(OrganizationProps),
  assets: PropTypes.arrayOf(AssetProps),
  assetCount: PropTypes.number,
  states: PropTypes.objectOf(PropTypes.number),
  saveSearch: PropTypes.func.isRequired,
  fetchAssets: PropTypes.func.isRequired,
  fetchOrganizations: PropTypes.func.isRequired
};

export class StewardSearchPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      asset: props.match.params.assetType === 'recarea' ? {
        label: 'Recreation Area',
        code: 'recarea'
      } : {
          label: 'Facility',
          code: 'facility'
        }
    };

    this.setSort = this.setSort.bind(this);
    this.setPage = this.setPage.bind(this);

    this.saveSearch = this.saveSearch.bind(this);
    this.goSearch = this.goSearch.bind(this);
    this.goInsert = this.goInsert.bind(this);
  }

  componentDidMount() {
    if (this.props.states.organizations !== STATE_READY) {
      this.props.fetchOrganizations();
    }

    const newSearchParams = {};
    let set = false;

    if (this.props.searchQuery.assetTypes.length !== 1 || this.props.searchQuery.assetTypes[0] !== this.state.asset.code) {
      newSearchParams.assetTypes = [this.state.asset.code];
      set = true;
    }

    if (this.props.searchQuery.orgIds.length === 0 && this.props.currentUser) {
      newSearchParams.orgIds = [this.props.currentUser.apiKey.orgId];
      set = true;
    }

    if (set) {
      this.props.saveSearch({
        ...this.props.searchQuery,
        ...newSearchParams
      });
    }
    else {
      this.goSearch();
    }
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(this.props.searchQuery, prevProps.searchQuery)) {
      this.goSearch(this.props.searchQuery);
    }
  }

  setSort(isSorted, isAsc, column) {
    this.props.saveSearch({ ...this.props.searchQuery, sort: `${isAsc ? '' : '-'}${column}` })
      .then(this.goSearch);
  }

  setPage(page) {
    this.props.saveSearch({ ...this.props.searchQuery, page })
      .then(this.goSearch);
  }

  saveSearch(query) {
    this.props.saveSearch(query)
      .then(this.goSearch);
  }

  goSearch(searchQuery) {
    const query = searchQuery || this.props.searchQuery;

    if (query.orgIds.length === 0) {
      return;
    }

    if (this.props.states.assets === STATE_LOADING) {
      return;
    }

    this.props.fetchAssets(query)
      .catch(() => { }); // TODO Handle Error
  }

  goInsert(query) {
    const assetType = query.assetTypes[0];

    this.props.history.push(`/data-steward/${assetType}/new`);
  }

  render() {
    const { organization, organizations, assets, assetCount, searchQuery: { orgIds, sort, limit, page, terms } } = this.props;
    const { asset: { label, code } } = this.state;

    const isAsc = !/^-/.test(sort);
    const pages = Math.ceil(assetCount / limit);

    const orgName = organization ? organization.name : '';
    const orgOptions = orgsToOptions(organization ? flattenOrg(organization) : flattenOrgs(organizations));
    return (
      <div className={styles['search-page']}>
        <h1>{label} Search Results For {orgName}</h1>
        <StewardSearchForm
          id={code}
          name={label}
          terms={terms || ''}
          orgId={orgIds[0] || ''}
          orgOptions={orgOptions}
          onSearch={this.saveSearch}
          onInsert={this.goInsert}
          searchOnChange
          showLabel={false}
          showLimitSelect />
        {/* Search results table */}
        <div className={`${styles['search-results-table']} ${styles['margin-bottom']}`}>
          <table className="table-no-border">
            <thead>
              <tr>
                <SortableTableColumnHeader
                  name={`${label} Name`}
                  id="name"
                  isSorted={/name/.test(sort)}
                  isAsc={/name/.test(sort) && isAsc}
                  onSortToggle={this.setSort} />
                <SortableTableColumnHeader
                  name="Organization"
                  id="org_name"
                  isSorted={/org_name/.test(sort)}
                  isAsc={/org_name/.test(sort) && isAsc}
                  onSortToggle={this.setSort} />
                <SortableTableColumnHeader
                  name="Last Updated At"
                  id="updated"
                  isSorted={/updated/.test(sort)}
                  isAsc={/updated/.test(sort) && isAsc}
                  onSortToggle={this.setSort} />
                <th />
              </tr>
            </thead>
            <tbody>
              {assets.map((asset) => (
                <tr key={asset.id}>
                  <td>
                    <Link to={`/data-steward/${code}/${asset.id}/display`}>{asset.name || '-- missing name --'}</Link>
                  </td>
                  <td>{asset.org_name}</td>
                  <td>{asset.updated_at}</td>
                  <td>
                    <Link to={`/data-steward/${code}/${asset.id}/display`}>View</Link>
                    &nbsp;|&nbsp;
                  {asset.reservable
                      ? <a href={`https://manage.${process.env.DOMAIN}`}>Edit in the Hub</a>
                      : <Link to={`/data-steward/${code}/${asset.id}/edit`}>Edit</Link>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={styles.row}>
          <div className={`${styles.cell} ${styles.biggest}`}>
            <p>
              Showing {Math.min(assetCount, page * limit + 1)} - {Math.min(assetCount, page * limit + limit)} of {assetCount} entries
            </p>
          </div>
          <div className={`${styles.cell} ${styles['align-right']}`}>
            <Pagination onChange={this.setPage} activePage={page} pages={pages} wings={2} />
          </div>
        </div>
      </div>
    );
  }
}

StewardSearchPage.propTypes = propTypes;

const mapStateToProps = (state) => ({
  currentUser: getCurrentUser(state),
  searchQuery: getSearch(state),
  organization: getOrganization(state, getCurrentUser(state).apiKey.orgId),
  organizations: getOrganizations(state),
  assets: getAssets(state),
  assetCount: getAssetCount(state),
  states: getState(state, 'assets', 'organizations')
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ saveSearch, fetchAssets, fetchOrganizations }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(StewardSearchPage);
